import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const hasValidUserData = useRef(false);

  // Fetch user profile data including subscription status
  const fetchUserProfile = async (authUser) => {
    if (!authUser) return null;
    
    try {
      // Add a timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Profile fetch timeout')), 5000); // 5 second timeout
      });
      
      const profilePromise = supabase
        .from('profiles')
        .select('*')
        .eq('id', authUser.id)
        .single();
      
      const { data, error } = await Promise.race([profilePromise, timeoutPromise]);
      
      if (error && error.code !== 'PGRST116') { // PGRST116 is "not found"
        console.error('Error fetching user profile:', error);
      }
      
      // Debug: Log the raw database data
      console.log('AuthContext: Raw database data for user', authUser.id, ':', data);
      
      // Merge auth user data with profile data
      const userWithProfile = {
        ...authUser,
        subscription: data?.subscription || 'free',
        subscription_status: data?.subscription_status || 'free',
        subscription_end_date: data?.subscription_end_date,
        stripe_customer_id: data?.stripe_customer_id,
        subscription_id: data?.subscription_id,
      };
      
      // Debug: Log the merged user data
      console.log('AuthContext: Merged user data:', {
        subscription: userWithProfile.subscription,
        subscription_status: userWithProfile.subscription_status,
        subscription_end_date: userWithProfile.subscription_end_date,
        stripe_customer_id: userWithProfile.stripe_customer_id,
        subscription_id: userWithProfile.subscription_id
      });
      
      hasValidUserData.current = true;
      return userWithProfile;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      // Don't fall back to basic user data - keep existing profile data if available
      // This prevents subscription status from flickering
      return null;
    }
  };

  // On mount, check for session
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session?.user?.id);
        
        if (session?.user) {
          const userWithProfile = await fetchUserProfile(session.user);
          if (userWithProfile) {
            setUser(userWithProfile);
          } else if (!hasValidUserData.current) {
            // Only set basic user data if we don't have valid profile data yet
            setUser({
              ...session.user,
              subscription: 'free',
            });
          }
          // If profile fetch fails and we already have valid data, don't update
        } else {
          setUser(null);
          hasValidUserData.current = false;
        }
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, [supabase.auth]); // Remove user from dependencies to prevent infinite re-renders

  const login = async (email, password) => {
    setLoading(true);
    
    try {
      const { error, data } = await supabase.auth.signInWithPassword({ email, password });
      
      if (error) {
        console.error('Login error:', error);
        setLoading(false);
        return false;
      }
      
      if (!data || !data.user) {
        console.error('No user data returned');
        setLoading(false);
        return false;
      }
      
      // Try to fetch profile, but don't let it block login
      try {
        const userWithProfile = await fetchUserProfile(data.user);
        if (userWithProfile) {
          setUser(userWithProfile);
        } else {
          // Only set basic user data if profile fetch completely fails
          setUser({
            ...data.user,
            subscription: 'free',
          });
        }
      } catch (profileError) {
        console.error('Error fetching profile, using basic user data:', profileError);
        setUser({
          ...data.user,
          subscription: 'free',
        });
      }
      
      setLoading(false);
      return true;
    } catch (err) {
      console.error('Unexpected error during login:', err);
      setLoading(false);
      return false;
    }
  };

  const register = async (email, password, plan) => {
    setLoading(true);
    const { error, data } = await supabase.auth.signUp({ email, password });
    if (error) {
      console.error('Registration error:', error);
      setLoading(false);
      return { success: false, error: error.message };
    }
    
    // Check if email confirmation is required
    if (data.user && !data.session) {
      // Email confirmation required
      setLoading(false);
      return { 
        success: true, 
        requiresConfirmation: true, 
        message: 'Please check your email and click the confirmation link to complete registration.' 
      };
    }
    
    // Profile should be created automatically by the database trigger
    // Just fetch the user profile to ensure it exists
    if (data.user) {
      try {
        console.log('Fetching profile for user:', data.user.id);
        const userWithProfile = await fetchUserProfile(data.user);
        setUser(userWithProfile);
        setLoading(false);
        return { success: true, requiresConfirmation: false, user: userWithProfile };
      } catch (profileError) {
        console.error('Error fetching profile after registration:', profileError);
        // Fall back to basic user data if profile fetch fails
        setUser({
          ...data.user,
          subscription: 'free',
          subscription_status: 'free',
        });
        setLoading(false);
        return { success: true, requiresConfirmation: false, user: data.user };
      }
    }
    
    setLoading(false);
    return { success: true, requiresConfirmation: false, user: data.user };
  };

  const logout = async () => {
    setLoading(true);
    await supabase.auth.signOut();
    setUser(null);
    setLoading(false);
  };

  // Function to refresh user profile (useful after subscription changes)
  const refreshUser = async () => {
    if (user) {
      try {
        const userWithProfile = await fetchUserProfile(user);
        if (userWithProfile) {
          setUser(userWithProfile);
          return true;
        }
      } catch (error) {
        console.error('Error refreshing user profile:', error);
      }
    }
    return false;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
} 